import { useEffect, useMemo, useState } from 'react';
import type { PartnerCatalogueRow } from '@dru/types';
import { SAMPLE_TYPE_LABEL, formatPaise } from '@dru/types';
import {
  Alert, Button, DetailSection, IconAction, InfoTip, Input, SearchInput, StatusBadge, TextField, cn, useNarrow,
} from '@dru/ui';
import { Pause, Play } from 'lucide-react';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';
import { formatDate } from '../../../lib/format';
import { Dialog } from '../../../components/Dialog';

/**
 * The shared phone stacking, held until `lg`: eight columns of inputs need
 * about 840px, so a portrait tablet cut the Availability column off.
 * Literal strings, because Tailwind only builds classes it can read.
 */
const STACK = {
  table: 'max-lg:block',
  head: 'max-lg:hidden',
  body: 'max-lg:block',
  row: 'max-lg:block max-lg:px-3 max-lg:py-2.5',
  first: 'max-lg:block max-lg:px-0 max-lg:pb-1',
  cell: 'max-lg:flex max-lg:min-h-9 max-lg:items-center max-lg:justify-between max-lg:gap-3 max-lg:px-0 max-lg:py-1 '
    + 'max-lg:text-left max-lg:before:shrink-0 max-lg:before:text-caption max-lg:before:font-normal '
    + 'max-lg:before:text-neutral-500 max-lg:before:content-[attr(data-label)]',
} as const;

/**
 * The venue's catalogue: every test it runs, on its own terms.
 *
 * Shown to the platform on the venue's page and to the venue on its own
 * profile, with the same table, because the two read the same row. What
 * differs is who may write what: the LIST of tests is the platform's (see
 * the capability picker), the TERMS — the lab's own code, its turnaround,
 * NABL scope, home collection, a pause — are the venue's own knowledge, and
 * either side may edit them here.
 *
 * No price column. What the venue is paid is a rate card, versioned and
 * never edited underneath a settlement; it is not something to type in a
 * grid next to a turnaround.
 */
export function VenueCatalogue({ partnerId, rows, canEdit, canImport, onSaved }: {
  partnerId: string;
  rows: PartnerCatalogueRow[];
  canEdit: boolean;
  /** Only the platform imports: an import can ADD tests, which is routing's business. */
  canImport: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, Draft>>({});
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pausing, setPausing] = useState<PartnerCatalogueRow | null>(null);
  // A phone gets the table as stacked rows, twenty at a time.
  const narrow = useNarrow();
  const [shown, setShown] = useState(20);
  const [importing, setImporting] = useState(false);

  // A reload from the server discards local edits — they were just saved, or
  // somebody else changed the list underneath us and stale keystrokes are
  // worse than lost ones.
  useEffect(() => { setDraft({}); }, [rows]);

  const q = search.trim().toLowerCase();
  const visible = useMemo(() => (q
    ? rows.filter((r) => `${r.name} ${r.code} ${r.partnerCode ?? ''} ${r.category}`.toLowerCase().includes(q))
    : rows), [rows, q]);

  const changed = Object.entries(draft).filter(([id, d]) => {
    const row = rows.find((r) => r.id === id);
    return row && (
      (d.partnerCode ?? '') !== (row.partnerCode ?? '')
      || tatHoursOf(row) !== (d.tatHours ?? '')
      || d.nablScope !== row.nablScope
      || d.homeCollection !== row.homeCollection
    );
  });

  function edit(row: PartnerCatalogueRow, patch: Partial<Draft>) {
    setDraft((d) => ({ ...d, [row.id]: { ...(d[row.id] ?? fromRow(row)), ...patch } }));
  }
  const of = (row: PartnerCatalogueRow): Draft => draft[row.id] ?? fromRow(row);

  async function save() {
    setBusy(true); setError(null);
    try {
      const bad = changed.find(([, d]) => d.tatHours.trim() !== '' && !(Number(d.tatHours) > 0));
      if (bad) throw new Error('Turnaround is a number of hours, or blank when it is not promised.');
      await api.patch(`/partners/${partnerId}/catalogue`, {
        rows: changed.map(([testId, d]) => ({
          testId,
          partnerCode: d.partnerCode.trim() || null,
          tatMinutes: d.tatHours.trim() === '' ? null : Math.round(Number(d.tatHours) * 60),
          nablScope: d.nablScope,
          homeCollection: d.homeCollection,
        })),
      });
      onSaved();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  async function liftPause(row: PartnerCatalogueRow) {
    setBusy(true); setError(null);
    try {
      await api.patch(`/partners/${partnerId}/catalogue`, { rows: [{ testId: row.id, pausedUntil: null }] });
      onSaved();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <DetailSection
      // Seventy-two tests: on a phone the list opens when it is asked for.
      phoneFolded
      title={`Catalogue (${rows.length})`}
      action={(
        <div className="flex items-center gap-2">
          <InfoTip label="About the catalogue">
            The venue&rsquo;s own code is what its report and invoice will print.
            Turnaround is promised from receipt of the sample. A paused test stays
            on the list and is skipped by routing until the date passes. What the
            venue is paid is not typed here: it comes from the rate card in force,
            so a change is a new card and the history keeps.
          </InfoTip>
          {canImport && (
            <Button size="sm" variant="secondary" onClick={() => setImporting(true)} disabled={busy}>
              Import CSV
            </Button>
          )}
          {canEdit && (
            <Button size="sm" loading={busy} onClick={save} disabled={changed.length === 0}>
              {changed.length ? `Save ${changed.length} row${changed.length === 1 ? '' : 's'}` : 'No changes'}
            </Button>
          )}
        </div>
      )}
    >
      {error && <Alert className="mt-2">{error}</Alert>}

      {rows.length === 0 ? (
        <p className="mt-3 text-small text-neutral-500">
          Nothing on the list yet. {canImport ? 'Tick tests above, or import the lab’s CSV.' : 'The platform sets which tests this venue runs.'}
        </p>
      ) : (
        <>
          <div className="mt-3">
            <SearchInput value={search} onValueChange={setSearch} placeholder="Test, our code or the venue's code" aria-label="Search the catalogue" className="sm:max-w-md" />
          </div>
          <div className="mt-2 overflow-x-auto rounded border border-neutral-100">
            <table className={cn('w-full text-sm', STACK.table)}>
              <thead className={cn('bg-neutral-50 text-left text-caption uppercase tracking-wide text-neutral-600', STACK.head)}>
                <tr>
                  <th className="w-10 px-3 py-2" aria-label="Row number">#</th>
                  <th className="px-3 py-2">Test</th>
                  <th className="px-3 py-2">Sample</th>
                  <th className="px-3 py-2">Venue&rsquo;s code</th>
                  <th className="px-3 py-2">TAT (hours)</th>
                  <th className="px-3 py-2 text-center">NABL</th>
                  <th className="px-3 py-2 text-center">Home</th>
                  <th className="px-3 py-2 text-right" title="From the payee rate card in force today. Set under Rates.">Paid today</th>
                  <th className="px-3 py-2">Availability</th>
                </tr>
              </thead>
              <tbody className={cn('divide-y divide-neutral-100', STACK.body)}>
                {(narrow ? visible.slice(0, shown) : visible).map((row, idx) => {
                  const d = of(row);
                  return (
                    <tr key={row.id} className={cn(row.paused && 'bg-warning-50/40', STACK.row)}>
                      <td className="hidden w-10 px-3 py-2 text-caption tabular-nums text-neutral-500 md:table-cell">{idx + 1}</td>
                      <td className={cn('px-3 py-2', STACK.first)}>
                        <span className="block text-neutral-900"><span className="mr-1.5 text-caption tabular-nums text-neutral-400 md:hidden">{idx + 1}.</span>{row.name}</span>
                        <span className="block font-mono text-caption text-neutral-500">{row.code}</span>
                      </td>
                      <td className={cn('px-3 py-2 text-xs text-neutral-600', STACK.cell)} data-label="Sample">
                        {row.sampleType === 'NONE' ? 'At a station' : (SAMPLE_TYPE_LABEL[row.sampleType] ?? row.sampleType)}
                      </td>
                      <td className={cn('px-3 py-2', STACK.cell)} data-label="Venue’s code">
                        {canEdit ? (
                          <Input value={d.partnerCode} onChange={(e) => edit(row, { partnerCode: e.target.value })}
                            className="w-32 font-mono text-xs" placeholder="—" aria-label={`Venue code for ${row.name}`} />
                        ) : <span className="font-mono text-xs">{row.partnerCode ?? <span className="text-neutral-500">—</span>}</span>}
                      </td>
                      <td className={cn('px-3 py-2', STACK.cell)} data-label="TAT (hours)">
                        {canEdit ? (
                          <Input value={d.tatHours} onChange={(e) => edit(row, { tatHours: e.target.value })}
                            className="w-20 text-xs" inputMode="decimal" placeholder="—" aria-label={`Turnaround for ${row.name}`} />
                        ) : <span className="text-xs">{tatHoursOf(row) || <span className="text-neutral-500">—</span>}</span>}
                      </td>
                      <td className={cn('px-3 py-2 text-center', STACK.cell)} data-label="NABL">
                        <Input type="checkbox" checked={d.nablScope} disabled={!canEdit} className="pointer-coarse:h-6 pointer-coarse:w-6"
                          onChange={(e) => edit(row, { nablScope: e.target.checked })} aria-label={`NABL scope for ${row.name}`} />
                      </td>
                      <td className={cn('px-3 py-2 text-center', STACK.cell)} data-label="Home collection">
                        <Input type="checkbox" checked={d.homeCollection} disabled={!canEdit} className="pointer-coarse:h-6 pointer-coarse:w-6"
                          onChange={(e) => edit(row, { homeCollection: e.target.checked })} aria-label={`Home collection for ${row.name}`} />
                      </td>
                      <td className={cn('px-3 py-2 text-right tabular-nums text-xs text-neutral-700', STACK.cell)} data-label="Paid today">
                        {row.rateTodayPaise == null ? <span className="text-neutral-500" title="No rate card covers this test today">—</span> : formatPaise(row.rateTodayPaise)}
                      </td>
                      <td className={cn('px-3 py-2', STACK.cell)} data-label="Availability">
                        {row.paused ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <StatusBadge tone="dueSoon">Paused until {row.pausedUntil ? formatDate(row.pausedUntil) : '—'}</StatusBadge>
                            {row.pausedReason && <span className="text-xs text-neutral-600">{row.pausedReason}</span>}
                            {canEdit && (
                              <IconAction label={`Resume ${row.name}`} icon={Play} onClick={() => liftPause(row)} disabled={busy} />
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <StatusBadge tone="covered">Available</StatusBadge>
                            {canEdit && (
                              <IconAction label={`Pause ${row.name}`} icon={Pause} onClick={() => setPausing(row)} disabled={busy} />
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Twenty at a time on a phone: seventy-two stacked tests are a long way to thumb. */}
            {narrow && visible.length > shown && (
              <div className="border-t border-neutral-100 p-3">
                <Button variant="secondary" className="w-full" onClick={() => setShown((n) => n + 20)}>
                  Show 20 more · {visible.length - shown} not shown
                </Button>
              </div>
            )}
          </div>
          {q && visible.length === 0 && (
            <p className="mt-2 text-small text-neutral-500">No test matches &ldquo;{search.trim()}&rdquo;.</p>
          )}
        </>
      )}

      {pausing && (
        <PauseDialog partnerId={partnerId} row={pausing} onClose={() => setPausing(null)}
          onDone={() => { setPausing(null); onSaved(); }} />
      )}
      {importing && (
        <ImportDialog partnerId={partnerId} onClose={() => setImporting(false)}
          onDone={() => { setImporting(false); onSaved(); }} />
      )}
    </DetailSection>
  );
}

interface Draft { partnerCode: string; tatHours: string; nablScope: boolean; homeCollection: boolean }

function tatHoursOf(row: PartnerCatalogueRow): string {
  if (row.tatMinutes == null) return '';
  const h = row.tatMinutes / 60;
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}
function fromRow(row: PartnerCatalogueRow): Draft {
  return {
    partnerCode: row.partnerCode ?? '', tatHours: tatHoursOf(row),
    nablScope: row.nablScope, homeCollection: row.homeCollection,
  };
}

/** "Analyzer away for service until Friday." The date is inclusive. */
function PauseDialog({ partnerId, row, onClose, onDone }: {
  partnerId: string; row: PartnerCatalogueRow; onClose: () => void; onDone: () => void;
}) {
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setError(null);
    try {
      await api.patch(`/partners/${partnerId}/catalogue`, {
        rows: [{ testId: row.id, pausedUntil: until, pausedReason: reason.trim() }],
      });
      onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  return (
    <Dialog title={`Pause ${row.name}`} onClose={onClose}>
      <p className="mt-1 flex items-center gap-1 text-xs text-neutral-500">
        Routing will not send an employee here for this test until the date has passed.
        <InfoTip label="About pausing a test">
          The test stays on the list, so nothing has to be re-declared afterwards.
        </InfoTip>
      </p>
      <TextField label="Paused until (inclusive)" type="date" value={until} onChange={setUntil} autoFocus />
      <TextField label="Why" value={reason} onChange={setReason}
        hint="What the venue told us: analyzer under service, reagent out of stock." />
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={submit} loading={busy} disabled={!until || reason.trim().length < 3}>Pause</Button>
      </div>
    </Dialog>
  );
}

/**
 * The lab's list, pasted or picked as a file. Adds tests that are missing and
 * updates the rest; refused whole on any bad row, with the rows named.
 */
function ImportDialog({ partnerId, onClose, onDone }: {
  partnerId: string; onClose: () => void; onDone: () => void;
}) {
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ rows: number; added: number; updated: number } | null>(null);

  async function pick(file: File | undefined) {
    if (file) setCsv(await file.text());
  }
  async function submit() {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ imported: { rows: number; added: number; updated: number } }>(
        `/partners/${partnerId}/catalogue/import`, { csv }, crypto.randomUUID());
      setResult(r.imported);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog title="Import the venue's catalogue" onClose={result ? onDone : onClose}>
      <p className="mt-1 text-xs text-neutral-500">
        A header row, then one row per test. <span className="font-mono">test_code</span> is
        ours or a LOINC number; the rest are optional:
        <span className="font-mono"> partner_code, tat_hours, nabl, home_collection, notes</span>.
        A test not yet on the list is added. Nothing is written if any row is wrong.
      </p>
      <pre className="mt-2 overflow-x-auto rounded bg-neutral-50 p-2 text-caption text-neutral-700">{'test_code,partner_code,tat_hours,nabl\nPAT-HB,CBC-LP22,4,yes\n718-7,HB-01,6,no'}</pre>
      <label className="mt-3 block text-xs text-neutral-700">
        File
        <input type="file" accept=".csv,text/csv" className="mt-1 block text-xs" onChange={(e) => pick(e.target.files?.[0])} />
      </label>
      <label className="mt-3 block text-xs text-neutral-700">
        Or paste it
        <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6}
          className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5 font-mono text-xs" />
      </label>
      {error && <Alert className="mt-3">{error}</Alert>}
      {result && (
        <Alert tone="success" className="mt-3">
          {result.rows} rows read: {result.added} added, {result.updated} updated.
        </Alert>
      )}
      <div className="mt-5 flex justify-end gap-2">
        {result ? (
          <Button onClick={onDone}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={submit} loading={busy} disabled={csv.trim().length === 0}>Import</Button>
          </>
        )}
      </div>
    </Dialog>
  );
}
